import { EnterOutlined, LoadingOutlined } from '@ant-design/icons';
import { Input } from 'antd';
import { useState, type KeyboardEvent } from 'react';

interface Props {
  onSend: (content: string) => void;
  disabled?: boolean;
  isPending?: boolean;
}

/**
 * Поле ввода тест-чата.
 *
 * Раскладка клавиш (как в мессенджерах):
 * - Enter — отправить
 * - Shift+Enter — перенос строки
 *
 * Вместо кнопки «Отправить» — значок ↵ в правом нижнем углу поля (по нему
 * тоже можно кликнуть). Пока ответ не пришёл (isPending), вместо значка —
 * спиннер, Enter игнорируется: защита от двойной отправки.
 */
export function MessageInput({ onSend, disabled, isPending }: Props) {
  const [value, setValue] = useState('');

  const trimmed = value.trim();
  const canSend = trimmed.length > 0 && !disabled && !isPending;

  const handleSend = () => {
    if (!canSend) return;
    onSend(trimmed);
    setValue('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // isComposing — ввод через IME (иероглифы и т.п.): Enter подтверждает слово.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      style={{
        padding: '12px 16px',
        borderTop: '1px solid #E5E7EB',
        background: '#FFFFFF',
      }}
    >
      <div style={{ position: 'relative' }}>
        <Input.TextArea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Напишите сообщение… (Shift+Enter — новая строка)"
          autoSize={{ minRows: 1, maxRows: 6 }}
          disabled={disabled}
          style={{ resize: 'none', paddingRight: 40 }}
        />
        <span
          role="button"
          aria-label="Отправить"
          tabIndex={-1}
          onClick={handleSend}
          style={{
            position: 'absolute',
            right: 12,
            bottom: 7,
            fontSize: 16,
            lineHeight: 1,
            color: canSend ? '#1F2937' : '#9CA3AF',
            cursor: canSend ? 'pointer' : 'default',
            userSelect: 'none',
          }}
        >
          {isPending ? <LoadingOutlined /> : <EnterOutlined />}
        </span>
      </div>
    </div>
  );
}
